import { clsx } from "clsx";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { RVIcon } from "@regal-voice/ui";
import { ActiveUsersLink } from "Pages/orchestration/Pages/journeys/ActiveUsers/Components/ActiveUsersLink";
import { JOURNEY_USER_COUNT_CUTOFF_DATE } from "Pages/orchestration/Pages/journeys/constants";
import { selectJourneyMetaData } from "Services/state/journeys/JourneyStateSlice";
import { NodeData } from "Types/Journey";

import styles from "./NodeUserCount.module.scss";

export function NodeUserCount({
    nodeData,
    display,
    className = "",
    viewingVersion,
}: {
    nodeData: NodeData;
    display?: boolean;
    className?: string;
    viewingVersion?: boolean;
}): JSX.Element {
    const journeyMetaData = useSelector(selectJourneyMetaData);
    const [searchParams] = useSearchParams();
    const isViewingVersion = viewingVersion ?? searchParams.get("version") !== null;

    const { originalCreatedAt, id: journeyId } = journeyMetaData;
    const { analyticsData, nodeId } = nodeData;
    // Set floor of 0 for user count, some backend errors can occasionally cause a negative number
    const users = Math.max(analyticsData?.currentUserCount || 0, 0);
    const inactive = users == 0;

    // We want to hide this user count feature for any journey created prior to 07/19/2022,
    // the date at which we've determined this data is accurately available for all journeys
    const journeyCreatedAfterCutoff =
        !!originalCreatedAt && new Date(originalCreatedAt) > new Date(JOURNEY_USER_COUNT_CUTOFF_DATE);

    if (!display || nodeData?.empty || !journeyCreatedAfterCutoff) {
        return <></>;
    }

    // Show "Not available" text when viewing a version instead of user counts
    if (isViewingVersion) {
        return (
            <div className={clsx(styles.userCountContainer, className)}>
                <span className={styles.notAvailableText}>Not available</span>
            </div>
        );
    }

    function userCountText(): string {
        return `${users.toLocaleString()} contact${users === 1 ? "" : "s"} here`;
    }

    return (
        <ActiveUsersLink journeyId={journeyId} nodeId={nodeId}>
            <div className={clsx(styles.userCountContainer, className)}>
                <span className={clsx(styles.dot, { [styles.inactive]: inactive })}></span>
                <span className={styles.userCountLink}>{userCountText()}</span>
                <RVIcon className={styles.icon} icon="arrow-right" color="purple-5" />
            </div>
        </ActiveUsersLink>
    );
}
